import { classNames } from "@/shared/utils/classnames";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { ButtonHTMLAttributes, Fragment } from "react";

export const buttonSizes: any = {
  xs: "px-2 py-1 text-xs h-6",
  sm: "px-2 py-1 text-sm h-8",
  md: "px-2.5 py-1.5 text-base h-10",
  lg: "px-3 py-2 text-lg h-12",
  xl: "px-3.5 py-2.5 text-xl h-16",
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  primary?: boolean;
  text?: string;
  size?: keyof typeof buttonSizes;
  iconLeft?: IconDefinition;
  iconRight?: IconDefinition;
  isLoading?: boolean;
  icon?: IconDefinition;
  tooltip?: string;
};

export const Button: React.FC<ButtonProps> = ({
  primary,
  size = "md",
  iconLeft,
  iconRight,
  text,
  children,
  isLoading,
  className,
  icon,
  tooltip,
  ...props
}) => {
  const sizeClassName = buttonSizes[size];

  return (
    <Fragment>
      <button
        {...props}
        type="button"
        className={classNames(
          `flex items-center rounded-md font-semibold gap-2 cursor-pointer`,
          primary
            ? "ring-primary-300 bg-primary-500 hover:bg-primary-400 text-white"
            : "ring-gray-300 hover:bg-gray-50 text-gray-900 ",
          icon ? "aspect-square" : "ring-1 ring-inset shadow-sm",
          sizeClassName,
          className,
          tooltip && "relative",
          `disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 disabled:ring-gray-200`
        )}
      >
        {tooltip && (
          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max bg-gray-900 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity z-50">
            {tooltip}
          </span>
        )}
        {iconLeft && <FontAwesomeIcon icon={iconLeft} className="h-4 w-4" />}
        {icon ? <FontAwesomeIcon icon={icon} /> : (text ?? children)}
        {iconRight && <FontAwesomeIcon icon={iconRight} className="h-4 w-4" />}
        {isLoading && (
          <div
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
            role="status"
          ></div>
        )}
      </button>
    </Fragment>
  );
};
